import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { SubjectAreaCode } from '@dosfilos/domain';
import { AREA_COLOR, NO_AREA_COLOR } from '../../../lib/subject-area-colors';

// ─── Malla view (read-only) ───────────────────────────────────────────────────
// Classic curriculum grid: one column per term, course cards stacked, with
// prerequisite connectors drawn as an SVG overlay. Cards are colored by CAP
// subject area (surfacing coverage/gaps at a glance) and show TCU; columns and
// the whole map sum their TCU. Editing stays in CurriculumMapEditor.

export interface MallaCourseMeta {
  id: string;
  name: string;
  code: string;
  /** Course ids this course depends on (drawn as arrows). */
  prerequisites: string[];
  /** From the course's CCD (`courseCcds/{courseId}`). Absent → no CCD yet
   *  (flagged). */
  area?: SubjectAreaCode;
  tcu?: number;
  /** An inactive course cannot be offered, so a plan that lists one has a hole
   *  in it: no section can ever be opened for that requirement. */
  inactive?: boolean;
}

export interface MallaTerm {
  termOffset: number;
  label?: string;
  recommendedCourseIds: string[];
  electiveSlots?: number;
}

// Area colors live in lib/subject-area-colors (shared with the ladder PDF).
export { AREA_COLOR, NO_AREA_COLOR };

interface MallaViewProps {
  terms: MallaTerm[];
  courseMeta: Map<string, MallaCourseMeta>;
}

export function MallaView({ terms, courseMeta }: MallaViewProps): JSX.Element {
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const theme = useTheme();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const [edges, setEdges] = useState<string[]>([]);

  const sortedTerms = [...terms].sort((a, b) => a.termOffset - b.termOffset);
  const idsInMalla = new Set(sortedTerms.flatMap((tm) => tm.recommendedCourseIds));

  // Recompute prerequisite connector paths from the laid-out card positions.
  const recomputeEdges = useCallback(() => {
    const inner = containerRef.current;
    if (!inner) return;
    const base = inner.getBoundingClientRect();
    const paths: string[] = [];
    idsInMalla.forEach((courseId) => {
      const meta = courseMeta.get(courseId);
      if (!meta) return;
      for (const prereqId of meta.prerequisites) {
        if (!idsInMalla.has(prereqId)) continue; // prereq not in this malla → skip
        const from = cardRefs.current.get(prereqId);
        const to = cardRefs.current.get(courseId);
        if (!from || !to) continue;
        const fr = from.getBoundingClientRect();
        const tr = to.getBoundingClientRect();
        const sx = fr.right - base.left;
        const sy = fr.top - base.top + fr.height / 2;
        const tx = tr.left - base.left;
        const ty = tr.top - base.top + tr.height / 2;
        const dx = Math.max(28, Math.abs(tx - sx) / 2);
        paths.push(`M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`);
      }
    });
    setEdges(paths);
  }, [courseMeta, terms]);

  useLayoutEffect(() => {
    recomputeEdges();
    const inner = containerRef.current;
    if (!inner || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recomputeEdges());
    ro.observe(inner);
    return () => ro.disconnect();
  }, [recomputeEdges]);

  if (sortedTerms.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography color="text.secondary">{t('program.curriculum.malla.empty')}</Typography>
      </Paper>
    );
  }

  const colorFor = (area?: SubjectAreaCode) => (area ? AREA_COLOR[area] : NO_AREA_COLOR);
  const totalTcu = sortedTerms.reduce(
    (sum, tm) =>
      sum + tm.recommendedCourseIds.reduce((s, id) => s + (courseMeta.get(id)?.tcu ?? 0), 0),
    0,
  );
  const usedAreas = Array.from(
    new Set(
      sortedTerms.flatMap((tm) =>
        tm.recommendedCourseIds.map((id) => courseMeta.get(id)?.area).filter(Boolean),
      ),
    ),
  ) as SubjectAreaCode[];

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            display: 'flex',
            gap: 2,
            width: 'max-content',
            minWidth: '100%',
            alignItems: 'flex-start',
          }}
        >
          {/* Prerequisite connectors — behind the cards, non-interactive. */}
          <Box
            component="svg"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              overflow: 'visible',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            <defs>
              <marker
                id="malla-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={theme.palette.text.disabled} />
              </marker>
            </defs>
            {edges.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke={theme.palette.text.disabled}
                strokeWidth={1.5}
                markerEnd="url(#malla-arrow)"
                opacity={0.7}
              />
            ))}
          </Box>

          {sortedTerms.map((tm, colIdx) => {
            const columnTcu = tm.recommendedCourseIds.reduce(
              (s, id) => s + (courseMeta.get(id)?.tcu ?? 0),
              0,
            );
            return (
              <Stack
                key={tm.termOffset}
                spacing={1.25}
                sx={{ width: 232, flexShrink: 0, zIndex: 1 }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ px: 0.5, color: 'text.secondary' }}
                >
                  {tm.label?.trim() ||
                    t('program.curriculum.termLabelPlaceholder', { n: colIdx + 1 })}
                </Typography>

                {tm.recommendedCourseIds.map((id) => {
                  const meta = courseMeta.get(id);
                  const color = colorFor(meta?.area);
                  const noCcd = !!meta && !meta.area && meta.tcu === undefined;
                  return (
                    <Paper
                      key={id}
                      ref={(el: HTMLDivElement | null) => {
                        if (el) cardRefs.current.set(id, el);
                        else cardRefs.current.delete(id);
                      }}
                      variant="outlined"
                      onClick={() => navigate(`/cap/ficha/${id}`)}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        borderLeft: `4px solid ${color}`,
                        opacity: noCcd ? 0.7 : 1,
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body2" fontWeight={700}>
                          {/* Sin metadatos la tarjeta mostraba el UUID como
                              título y como nombre: ilegible, y sin decir que el
                              problema es el curso, no la malla. */}
                          {meta?.code || t('program.curriculum.malla.unknownCourse')}
                        </Typography>
                        {meta?.tcu !== undefined && (
                          <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                            {t('program.curriculum.malla.tcu', { n: meta.tcu })}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {meta?.name ?? id}
                      </Typography>
                      {meta?.inactive && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={t('program.curriculum.malla.inactiveCourse')}
                          sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                        />
                      )}
                      {noCcd && (
                        <Tooltip title={tCap('ccd.openForCourse')} arrow>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('program.curriculum.malla.noCcd')}
                            sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                          />
                        </Tooltip>
                      )}
                    </Paper>
                  );
                })}

                {/* Elective slots as dashed placeholders. */}
                {Array.from({ length: tm.electiveSlots ?? 0 }).map((_, i) => (
                  <Box
                    key={`elec-${i}`}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      border: `1.5px dashed ${theme.palette.divider}`,
                      color: 'text.disabled',
                      fontSize: 13,
                    }}
                  >
                    {t('program.curriculum.malla.electiveSlot')}
                  </Box>
                ))}

                <Typography
                  variant="caption"
                  sx={{ px: 0.5, pt: 0.5, color: 'text.secondary', fontWeight: 600 }}
                >
                  {t('program.curriculum.malla.tcu', { n: columnTcu })}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      </Box>

      {/* Legend + total */}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={1}
        sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {t('program.curriculum.malla.legend')}
        </Typography>
        {usedAreas.map((area) => (
          <Chip
            key={area}
            size="small"
            label={tCap(`subjectAreas.${area}`)}
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: 'transparent',
              border: `1px solid ${AREA_COLOR[area]}`,
              '& .MuiChip-label': { color: AREA_COLOR[area] },
            }}
          />
        ))}
        <Box flex={1} />
        <Typography variant="body2" fontWeight={700}>
          {t('program.curriculum.malla.total', { n: totalTcu })}
        </Typography>
      </Stack>
    </Box>
  );
}
